<template>
  <Card class="tw-flex tw-flex-col tw-h-full tw-overflow-hidden">
    <div class="tw-flex tw-items-center tw-justify-between tw-p-6 tw-pb-5">
      <div class="tw-flex tw-items-center tw-gap-3">
        <h3 class="tw-text-base tw-font-semibold tw-leading-none tw-tracking-tight">
          {{ t('users.titles.users') }}
        </h3>
        <router-link
          class="tw-group tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-font-semibold tw-text-neutral-400 hover:tw-text-primary-300 tw-transition-colors tw-duration-200 tw-whitespace-nowrap"
          :to="{ name: 'list-users' }"
        >
          {{ t('common.data.view_all') }}
          <OpenLinkIcon
            class="tw-w-3.5 tw-h-3.5 tw-transition-colors tw-duration-200 group-hover:tw-text-primary-300"
          />
        </router-link>
      </div>
      <CustomerActionButton
        class="tw-flex tw-items-center tw-gap-2 tw-text-[13px] tw-text-neutral-900 tw-border-none tw-px-3.5 tw-py-1.5 tw-rounded-lg tw-cursor-pointer tw-font-semibold tw-bg-primary-250 hover:tw-bg-primary-300 tw-transition-colors tw-duration-200"
        @click="router.push({ name: 'create-user' })"
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 14 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10.7918 11.875H8.4585"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M9.625 13.0416V10.7083"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M7.09351 6.84085C7.03518 6.83502 6.96518 6.83502 6.90101 6.84085C5.51268 6.79419 4.41018 5.65669 4.41018 4.25669C4.40434 2.82752 5.56518 1.66669 6.99434 1.66669C8.42351 1.66669 9.58434 2.82752 9.58434 4.25669C9.58434 5.65669 8.47601 6.79419 7.09351 6.84085Z"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M6.99395 13.2225C5.93228 13.2225 4.87645 12.9542 4.07145 12.4175C2.65978 11.4725 2.65978 9.93252 4.07145 8.99335C5.67561 7.92002 8.30645 7.92002 9.91061 8.99335"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        {{ t('users.titles.create') }}
      </CustomerActionButton>
    </div>

    <CardContent class="tw-flex-1 tw-p-0">
      <Table>
        <TableHeader>
          <TableRow class="tw-bg-neutral-50/70 hover:tw-bg-neutral-50/70">
            <TableHead
              v-for="header in headers"
              :key="header.value"
              class="tw-px-6 tw-h-9 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-widest tw-text-neutral-400"
            >
              {{ header.text }}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow v-if="isLoading">
            <TableCell
              :colspan="headers.length"
              class="tw-h-32 tw-text-center tw-text-sm tw-text-neutral-400"
            >
              {{ t('common.data.loading') }}
            </TableCell>
          </TableRow>
          <TableRow v-else-if="items.length === 0">
            <TableCell
              :colspan="headers.length"
              class="tw-h-32 tw-text-center tw-text-sm tw-text-neutral-400"
            >
              {{ t('common.data.no_data') }}
            </TableCell>
          </TableRow>
          <template v-else>
            <TableRow v-for="(item, index) in items" :key="index">
              <TableCell class="tw-px-6 tw-py-3 tw-text-sm tw-font-medium tw-text-neutral-800">
                {{ item.first_name }}
              </TableCell>
              <TableCell class="tw-px-6 tw-py-3 tw-text-sm tw-font-medium tw-text-neutral-800">
                {{ item.last_name }}
              </TableCell>
              <TableCell class="tw-px-6 tw-py-3 tw-text-sm tw-text-neutral-500">
                {{ item.email }}
              </TableCell>
              <TableCell class="tw-px-6 tw-py-3 tw-text-sm tw-text-neutral-400 tw-tabular-nums">
                {{ item.created_on }}
              </TableCell>
            </TableRow>
          </template>
        </TableBody>
      </Table>
    </CardContent>

    <div class="tw-mt-auto tw-border-t tw-border-neutral-100">
      <CustomPagination
        v-model:current-page="serverOptions.page"
        v-model:rows-per-page="serverOptions.rowsPerPage"
        :total-items="usersListTotalCount"
        :rows-items="[5, 10, 20]"
      />
    </div>
  </Card>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, type Ref } from 'vue';
interface ServerOptions {
  page: number;
  rowsPerPage: number;
  sortBy?: string;
  sortType?: 'asc' | 'desc';
}
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import OpenLinkIcon from './icons/openLinkIcon.svg';
import { useRouter } from 'vue-router';
import { useUserStore } from '@/stores/user.store';
import moment from 'moment';
import { useI18n } from 'vue-i18n';
import CustomPagination from '@/components/shared/CustomPagination.vue';
import CustomerActionButton from '@/components/shared/CustomerActionButton.vue';
import cookieService from '@/_helpers/services/cookie.service';
const { t } = useI18n();
const router = useRouter();
const { getUsersList } = useUserStore();
const customerId = ref(-1);
const usersListTotalCount = ref(0);
const isLoading = ref(false);

const headers = [
  {
    text: t('users.titles.first_name'),
    value: 'first_name',
    sortable: true,
    width: 120,
  },
  {
    text: t('users.titles.last_name'),
    value: 'last_name',
    sortable: true,
    width: 120,
  },
  {
    text: t('common.titles.email'),
    value: 'email',
    sortable: true,
    width: 100,
  },
  {
    text: t('users.forms.created_at'),
    value: 'created_on',
    sortable: true,
  },
];

const items: Ref<any[]> = ref([]);
const serverOptions = ref<ServerOptions>({
  page: 1,
  rowsPerPage: 5,
  sortBy: 'first_name',
  sortType: 'asc',
});

const fetchUsers = async () => {
  let direction = serverOptions.value.sortType === 'asc' ? '' : '-';
  let ordering = `${direction}${serverOptions.value.sortBy}`;
  isLoading.value = true;
  const result = await getUsersList({
    customer_id: customerId.value,
    active: true,
    page: serverOptions.value.page,
    page_size: serverOptions.value.rowsPerPage,
    ordering: ordering,
  });
  isLoading.value = false;
  items.value = result.results.map(user => ({
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email,
    created_on: moment(user.created_on).format('DD.MM.YYYY'),
    profile_picture: user.profile_picture,
  }));
  usersListTotalCount.value = result.count;
};
onMounted(async () => {
  customerId.value = +cookieService.get('selectedCustomerId')!;
  await fetchUsers();
});

watch(
  serverOptions,
  async () => {
    await fetchUsers();
  },
  { deep: true }
);
</script>
